/* Keep merchant-authored tables keyboard scrollable without rewriting their content. */
(() => {
  // Manual scroll controls enhance the native overflow list; there is no autoplay.
  if ('customElements' in window && !customElements.get('mm-milestone-timeline')) {
    customElements.define('mm-milestone-timeline', class extends HTMLElement {
      connectedCallback() {
        if (this.cleanup) return;
        this.track = this.querySelector('[data-mm-timeline-track]');
        this.items = Array.from(this.querySelectorAll('[data-mm-milestone]'));
        if (!this.track || !this.items.length) return;
        this.cleanup = [];
        const listen = (target, type, listener, options) => {
          target.addEventListener(type, listener, options);
          this.cleanup.push(() => target.removeEventListener(type, listener, options));
        };
        this.previous = this.querySelector('[data-mm-timeline-prev]');
        this.next = this.querySelector('[data-mm-timeline-next]');
        this.status = this.querySelector('[data-mm-timeline-status]');
        this.controls = this.querySelector('[data-mm-timeline-controls]');
        listen(this.previous, 'click', () => this.move(this.nearestIndex() - 1));
        listen(this.next, 'click', () => this.move(this.nearestIndex() + 1));
        listen(this.track, 'scroll', () => {
          clearTimeout(this.scrollTimer);
          this.scrollTimer = setTimeout(() => this.update(), 120);
        }, { passive: true });
        listen(this.track, 'keydown', event => {
          if (event.target !== this.track || event.altKey || event.ctrlKey || event.metaKey) return;
          const rtl = getComputedStyle(this.track).direction === 'rtl';
          const movement = { ArrowLeft: rtl ? 1 : -1, ArrowRight: rtl ? -1 : 1 };
          if (event.key in movement) this.move(this.nearestIndex() + movement[event.key]);
          else if (event.key === 'Home') this.move(0);
          else if (event.key === 'End') this.move(this.items.length - 1);
          else return;
          event.preventDefault();
        });
        listen(document, 'shopify:block:select', event => {
          const index = this.items.findIndex(item => item.dataset.blockId === event.detail?.blockId);
          if (index >= 0) this.move(index, false);
        });
        if ('ResizeObserver' in window) {
          this.observer = new ResizeObserver(() => this.update());
          this.observer.observe(this.track);
        } else listen(window, 'resize', () => this.update());
        this.update();
      }
      offset(item) {
        const track = this.track.getBoundingClientRect();
        const rectangle = item.getBoundingClientRect();
        return getComputedStyle(this.track).direction === 'rtl' ? rectangle.right - track.right : rectangle.left - track.left;
      }
      nearestIndex() {
        return this.items.reduce((best, item, index) => Math.abs(this.offset(item)) < Math.abs(this.offset(this.items[best])) ? index : best, 0);
      }
      move(index, animate = true) {
        const item = this.items[Math.max(0, Math.min(index, this.items.length - 1))];
        const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        this.track.scrollTo({ left: this.track.scrollLeft + this.offset(item), behavior: animate && !reducedMotion ? 'smooth' : 'auto' });
      }
      update() {
        const limit = this.track.scrollWidth - this.track.clientWidth;
        const position = Math.abs(this.track.scrollLeft);
        this.controls.hidden = limit <= 2;
        this.previous.disabled = position <= 2;
        this.next.disabled = position >= limit - 2;
        const track = this.track.getBoundingClientRect();
        const visible = this.items.map((item, index) => ({ rectangle: item.getBoundingClientRect(), index }))
          .filter(({ rectangle }) => rectangle.right > track.left + 2 && rectangle.left < track.right - 2);
        if (visible.length) this.status.textContent = `${visible[0].index + 1}–${visible[visible.length - 1].index + 1} / ${this.items.length}`;
      }
      disconnectedCallback() {
        this.cleanup?.forEach(remove => remove());
        this.cleanup = null;
        this.observer?.disconnect();
        clearTimeout(this.scrollTimer);
      }
    });
  }
  const preparePolicy = policy => {
    if (policy.dataset.mmPolicyReady) return;
    const documentBody = policy.querySelector('.mm-policy-document');
    const sidebar = policy.querySelector('[data-mm-policy-sidebar]');
    const sectionList = sidebar?.querySelector('[data-mm-policy-sections]');
    if (!documentBody || !sidebar || !sectionList) return;
    policy.dataset.mmPolicyReady = 'true';
    // Generic policy pages already have a page H1; preserve any stored title
    // wording while making its subordinate document heading an H2.
    if (policy.querySelector('.mm-content-heading h1')) {
      documentBody.querySelectorAll('h1').forEach(original => {
        const heading = document.createElement('h2');
        Array.from(original.attributes).forEach(attribute => heading.setAttribute(attribute.name, attribute.value));
        heading.append(...Array.from(original.childNodes));
        original.replaceWith(heading);
      });
    }
    const sections = [];
    const firstBlock = documentBody.firstElementChild;
    Array.from(documentBody.children).forEach(block => {
      let heading;
      const text = block.textContent.trim();
      if (!text) return;
      // Original legal sources use bold paragraphs and one-item numbered lists
      // as section labels. Add heading semantics without rewriting their words.
      if (/^H[2-4]$/.test(block.tagName) && block !== firstBlock) heading = block;
      else if (block.tagName === 'P' && text.length < 160 &&
        block.children.length === 1 && /^(STRONG|B)$/.test(block.firstElementChild.tagName) &&
        block.firstElementChild.textContent.trim() === text) {
        block.setAttribute('role', 'heading');
        block.setAttribute('aria-level', '2');
        heading = block;
      } else if (block.tagName === 'OL' && block.children.length === 1 &&
        block.firstElementChild.tagName === 'LI' && text.length < 160 &&
        !block.querySelector('ol,ul')) {
        const item = block.firstElementChild;
        heading = document.createElement('h2');
        heading.append(...Array.from(item.childNodes));
        item.append(heading);
        block.classList.add('mm-policy-numbered-section');
        heading.dataset.policyNumber = item.getAttribute('value') || block.getAttribute('start') || '1';
      }
      if (!heading) return;
      heading.classList.add('mm-policy-section-title');
      if (!heading.id) {
        const base = 'mm-policy-section-' + (sections.length + 1);
        let id = base, suffix = 1;
        while (document.getElementById(id)) id = base + '-' + (++suffix);
        heading.id = id;
      }
      heading.tabIndex = -1;
      sections.push(heading);
    });
    documentBody.querySelectorAll('p').forEach(paragraph => {
      const text = paragraph.textContent.trim();
      const letters = text.replace(/[^a-zA-Z]/g, '');
      // A name such as “MuteMe LLC” can appear inside an otherwise capitalized notice.
      const capitals = letters.replace(/[^A-Z]/g, '').length;
      if (text.length > 100 && letters && capitals / letters.length >= .88) paragraph.classList.add('mm-policy-notice');
    });
    if (!sections.length) return;
    sections.forEach(heading => {
      const item = document.createElement('li');
      const link = document.createElement('a');
      link.href = '#' + heading.id;
      link.textContent = (heading.dataset.policyNumber ? heading.dataset.policyNumber + '. ' : '') + heading.textContent.trim();
      item.append(link);
      sectionList.append(item);
    });
    sidebar.querySelector('[data-mm-policy-meta]').textContent = sections.length + ' sections · ' + Math.max(1, Math.ceil(documentBody.textContent.trim().split(/\s+/).length / 220)) + ' min read';
    sidebar.querySelector('details').open = window.matchMedia ? window.matchMedia('(min-width: 1000px)').matches : true;
    sidebar.hidden = false;
    policy.querySelector('.mm-policy-layout').dataset.mmPolicyReady = 'true';
  };
  const prepareContent = (root = document) => {
    root.querySelectorAll('.mm-prose h1, .mm-prose h2, .mm-prose h3').forEach(heading => {
      if (!heading.textContent.trim() && !heading.querySelector('img,svg,iframe')) heading.remove();
    });
    root.querySelectorAll('.mm-policy-document p').forEach(paragraph => {
      if (!paragraph.textContent.trim() && !paragraph.querySelector('img,svg,iframe,video,input')) paragraph.remove();
    });
    root.querySelectorAll('.mm-policy-page').forEach(preparePolicy);
    if (root.matches?.('.mm-policy-page')) preparePolicy(root);
    root.querySelectorAll('[data-mm-library-filter]').forEach(input => {
      if (input.dataset.mmLibraryReady) return;
      input.dataset.mmLibraryReady = 'true';
      const library = input.closest('.mm-support-library');
      const articles = Array.from(library.querySelectorAll('[data-mm-support-article]'));
      const groups = Array.from(library.querySelectorAll('[data-mm-support-group]'));
      const status = library.querySelector('[data-mm-library-status]');
      const empty = library.querySelector('[data-mm-library-empty]');
      const normalize = value => value.toLocaleLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
      const update = () => {
        const terms = normalize(input.value).split(/\s+/).filter(Boolean);
        let visible = 0;
        articles.forEach(article => {
          const matches = terms.every(term => normalize(article.textContent).includes(term));
          article.hidden = !matches;
          if (matches) visible++;
        });
        groups.forEach(group => { group.hidden = !Array.from(group.querySelectorAll('[data-mm-support-article]')).some(article => !article.hidden); });
        status.textContent = visible + ' of ' + articles.length + ' support articles';
        empty.hidden = visible !== 0;
      };
      input.closest('.mm-support-filter').hidden = false;
      input.addEventListener('input', update);
      input.addEventListener('search', update);
      update();
    });
    root.querySelectorAll('button.toggle-forgetfulness').forEach(button => {
      if (button.dataset.mmKeyboardReady) return;
      button.dataset.mmKeyboardReady = 'true';
      button.addEventListener('click', event => {
        if (event.target === button) button.querySelector('span')?.click();
      });
    });
    root.querySelectorAll('.mm-prose table, .mm-article-page .post-content table').forEach(table => {
      if (table.scrollWidth > table.clientWidth) {
        table.tabIndex = 0;
        if (!table.getAttribute('aria-label')) table.setAttribute('aria-label', 'Scrollable table');
      }
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => prepareContent());
  else prepareContent();
  document.addEventListener('shopify:section:load', event => prepareContent(event.target));
})();
